import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import debounce from "lodash.debounce";
import { Button, Input, Modal, Spin, Tooltip } from "antd";
import { message } from "utils/ciq-message";
import { InfoCircleOutlined, DeleteOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { restrictInputToNumbers } from "utils/inpututils";
import { FeatureTypes } from "entity-app/constants";
import { TEntityMilestone, TFeatureWorkflowInstance } from "entity-app/models";
import { DateUtils } from "utils/dateutils";
import { useCIQMutation, useCIQQuery } from "hooks/ciq-gql-hooks";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import {
  DELETE_FEATURE_INSTANCE,
  MUTATION_CREATE_FEATURE_INSTANCE
} from "entity-app/graphQL/ciq-feature-mutations";
import { gql } from "@apollo/client";
import { ErrorMessages, ProjectPermissionEnum } from "constants/index";
import { TAuditChangeLog } from "change-events/change-event-polling";
import { hasTheFeature } from "entity-app/utils/utils";
import { QUERY_WORKFLOW_DEFAULT } from "services/graphQL/queries";
import { WorkflowDefaultData } from "pages/project-setting-menu/model";
import { TEntityDateblock } from "../../models";
import { APPLY_WORKFLOW_MUTATION } from "../../queries/mutation";

function NoSubmittalOrPlannedSubmittalLink({
  canEditLinkingPage,
  projectId,
  materialEntity,
  afterChange,
  isRecalculating,
  compact = false
}: Readonly<{
  canEditLinkingPage: boolean;
  projectId?: string;
  materialEntity?: TEntityDateblock;
  afterChange?: () => void;
  isRecalculating?: boolean;
  /**
   * When true, renders the card without the dateblock-specific outer
   * positioning wrapper (`pl-4 pt-16`) and the trailing connector line —
   * suitable for embedding in non-dateblock contexts (e.g. details pages
   * or other empty-state slots).
   */
  compact?: boolean;
}>) {
  const { gqlClientForProject, tokenContents, eventLogs } =
    useContext(ProjectContext);

  const [plannedSubmittalRefreshKey, setPlannedSubmittalRefreshKey] =
    useState(1);

  const [loading, setLoading] = useState(false);
  const [isUpdatingOffsets, setIsUpdatingOffsets] = useState(false);
  const isOffsetEditPendingRef = useRef(false);

  const cannotCreateSubmittal = isPermissionNotGrantted(
    ProjectPermissionEnum.CreateSubmittal,
    tokenContents?.role ?? ""
  );

  const [createPlannedSubmittalMutation] = useCIQMutation(
    gql(MUTATION_CREATE_FEATURE_INSTANCE),
    {
      client: gqlClientForProject
    }
  );

  const { data: workflowDefaultData, refetch: refetchWorkflowDefaultData } =
    useCIQQuery<{
      workflow_defaults: Array<{
        feature_id: FeatureTypes;
        float: number;
        workflow_data: Array<WorkflowDefaultData>;
      }>;
    }>(QUERY_WORKFLOW_DEFAULT, {
      client: gqlClientForProject,
      skip: !gqlClientForProject,
      variables: {
        where: {
          feature_id: { _eq: FeatureTypes.SUBMITTALS }
        }
      }
    });

  const [applyWorkflowMutation] = useCIQMutation(APPLY_WORKFLOW_MUTATION, {
    client: gqlClientForProject
  });

  const [deleteFeatureInstanceMutation] = useCIQMutation(
    gql(DELETE_FEATURE_INSTANCE),
    {
      client: gqlClientForProject
    }
  );

  const [plannedSubmittal, setPlannedSubmittal] = useState<{
    id: string | undefined;
    offsets: {
      id: string;
      name: string;
      duration: number;
    }[];
    milestonesDates: TEntityMilestone[];
    feature_workflow_instance: TFeatureWorkflowInstance;
  } | null>(null);

  useEffect(() => {
    // plannedSubmittalRefreshKey starts at 1 and is only ever incremented, so it
    // can never be negative -> this guard never fires.
    /* v8 ignore next */
    if (plannedSubmittalRefreshKey < 0) return;
    if (isOffsetEditPendingRef.current) return;
    const links = materialEntity?.links_as_target?.filter(
      (link) =>
        link?.source_feature_instance?.feature_type_id ===
        FeatureTypes.PLANNING_ONLY_SUBMITTAL
    );

    const submittalLink = links && links.length > 0 ? links[0] : null;
    if (submittalLink?.source_feature_instance) {
      const submittalSource: TEntityDateblock =
        submittalLink.source_feature_instance;
      const offsets: Array<{
        id: string;
        name: string;
        duration: number;
      }> = [];
      const milestonesDates: Array<TEntityMilestone> = [];
      const sortedMilestones = [
        ...submittalSource.workflow_instance_milestones
      ].sort((a, b) => a.sequence_no - b.sequence_no);
      sortedMilestones.forEach((milestone) => {
        milestonesDates.push(milestone);
        if (
          milestone.feature_workflow_instance_offsets_as_src &&
          milestone.feature_workflow_instance_offsets_as_src.length > 0
        ) {
          milestone.feature_workflow_instance_offsets_as_src.forEach(
            (offset) => {
              offsets.push(offset);
            }
          );
        }
      });

      setPlannedSubmittal({
        id: submittalSource.id,
        offsets,
        milestonesDates,
        feature_workflow_instance: submittalSource.feature_workflow_instance
      });
    } else {
      setPlannedSubmittal(null);
    }
  }, [materialEntity?.links_as_target, plannedSubmittalRefreshKey]);

  // Poll the changes and update the respective UI components
  const previousEventLogs = useRef(eventLogs);
  useEffect(() => {
    if (
      plannedSubmittal?.id &&
      eventLogs?.length &&
      previousEventLogs.current !== eventLogs
    ) {
      const featureInstanceId = plannedSubmittal.id;
      const conditionForBidAndDesignPackage = (log: TAuditChangeLog) =>
        hasTheFeature(featureInstanceId, log);
      if (eventLogs.some(conditionForBidAndDesignPackage)) {
        // Force plannedSubmittal recompute
        setTimeout(() => {
          setPlannedSubmittalRefreshKey((prev) => prev + 1);
        }, 1000);
      }
    }
    if (eventLogs?.length && previousEventLogs.current !== eventLogs) {
      const hasWorkflowConfigLog = eventLogs?.some(
        (log) =>
          log.project_id === projectId &&
          log.data_source === "project_feature_configurations"
      );
      if (hasWorkflowConfigLog) {
        refetchWorkflowDefaultData();
      }
    }
    previousEventLogs.current = eventLogs;
  }, [eventLogs, plannedSubmittal?.id, refetchWorkflowDefaultData, projectId]);

  const onOffsetDurationChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    offsetId: string
  ) => {
    const value = Number(e.target.value || 0);

    setPlannedSubmittal((prev) => {
      // onChange only fires from the duration inputs, which render only while a
      // plannedSubmittal exists, so `prev` is always non-null here.
      /* v8 ignore next */
      if (!prev) return prev;

      return {
        ...prev,
        offsets: prev.offsets.map((o) =>
          o.id === offsetId ? { ...o, duration: value } : o
        )
      };
    });

    isOffsetEditPendingRef.current = true;
    debouncedUpdateOffsets({ offsetId, duration: value });
  };

  const createPlannedSubmittal = async () => {
    const submittalWorkflowDefault =
      workflowDefaultData?.workflow_defaults?.find(
        (x) => x.feature_id === FeatureTypes.SUBMITTALS
      );
    if (!materialEntity || !submittalWorkflowDefault) {
      message.error(ErrorMessages.SomethingWentWrong);
      return;
    }

    setLoading(true);

    const prepDuration =
      submittalWorkflowDefault?.workflow_data?.find(
        (x) => x.offset_name === "SC Submittal Prep"
      )?.offset_value || 0;

    const reviewOffsetNames = new Set([
      "Design Review",
      "GC Review",
      "Final GC Review"
    ]);
    const totalReviewDuration =
      submittalWorkflowDefault?.workflow_data?.reduce((sum, item) => {
        return reviewOffsetNames.has(item.offset_name)
          ? sum + (item.offset_value || 0)
          : sum;
      }, 0) || 0;

    const variables = {
      feature_type: "PLANNING_ONLY_SUBMITTAL",
      feature_instances: [
        {
          title: "Submittal Plan",
          number: null,
          offsets: [
            { sequence: 1, duration: prepDuration },
            { sequence: 2, duration: totalReviewDuration }
          ],
          links_as_source: {
            target_instance_id: materialEntity.id
          }
        }
      ]
    };

    const response: any = await createPlannedSubmittalMutation({
      variables
    });
    if (response.success) {
      message.success("Submittal Plan created successfully.");
      afterChange?.();
    } else {
      message.error("Unable to create Submittal Plan.");
    }

    setTimeout(() => {
      setLoading(false);
    }, 3000);
  };

  const updatePlannedSubmittalOffsets = useCallback(
    async ({ offsetId, duration }: { offsetId: string; duration: number }) => {
      setIsUpdatingOffsets(true);
      try {
        const input = [
          {
            feature_instance_id:
              plannedSubmittal?.feature_workflow_instance.feature_instance_id,
            workflow_instance_id:
              plannedSubmittal?.feature_workflow_instance.id,
            offsets: [
              {
                id: offsetId,
                duration
              }
            ]
          }
        ];

        const variables = {
          input,
          metadata: {
            request_source: "DETAIL_PAGE"
          }
        };

        await applyWorkflowMutation({
          variables
        });
      } finally {
        setIsUpdatingOffsets(false);
        isOffsetEditPendingRef.current = false;
      }
    },
    [plannedSubmittal, applyWorkflowMutation]
  );

  const debouncedUpdateRef = useRef<ReturnType<typeof debounce>>();
  const debouncedUpdateOffsets = useMemo(() => {
    debouncedUpdateRef.current = debounce(updatePlannedSubmittalOffsets, 2000);
    return debouncedUpdateRef.current;
  }, [updatePlannedSubmittalOffsets]);

  useEffect(() => {
    return () => {
      debouncedUpdateRef.current?.cancel();
    };
  }, []);

  const deletePlannedSubmittal = async () => {
    if (!plannedSubmittal?.feature_workflow_instance.feature_instance_id)
      return;
    setLoading(true);

    const variables = {
      feature_instance_ids:
        plannedSubmittal?.feature_workflow_instance.feature_instance_id,
      feature_type_id:
        plannedSubmittal?.feature_workflow_instance.feature_type_id
    };
    const response: any = await deleteFeatureInstanceMutation({
      variables
    });
    if (response.success) {
      message.success("Submittal Plan removed successfully.");
      afterChange?.();
    } else {
      message.error("Unable to remove Submittal Plan.");
    }
    setTimeout(() => {
      setLoading(false);
    }, 3000);
  };

  const cardContent = (
    <div
      data-testid="no-submittal-card-content"
      className={
        compact
          ? "w-full min-w-0 overflow-hidden rounded border border-solid border-ciq-black-a10 bg-white flex flex-col"
          : "w-[250px] overflow-hidden rounded border border-solid border-ciq-black-a10 bg-white flex flex-col"
      }
    >
      <div className="flex justify-between items-center p-[8px] text-xs">
        <span className="font-semibold text-black text-opacity-90 text-xs">
          No Submittal Linked
        </span>
        {canEditLinkingPage && (
          <Link
            to={`/project/${projectId}/schedule/material-linking?&materialId=${materialEntity?.feature_workflow_instance?.feature_instance_id}&linkSubmittal=true`}
            data-testid="dsme-link-submittals-button"
            className=""
          >
            Link
          </Link>
        )}
      </div>
      <div className="border-0 border-t border-solid border-ciq-black-a10 p-[8px] text-xs">
        <div className="flex justify-between items-center pb-[8px]">
          <div className="font-semibold text-black text-opacity-90">
            Submittal Plan
            <Tooltip title="Enter durations of Prep and Review Time to create a plan.">
              <InfoCircleOutlined className="text-black text-opacity-50 ml-1" />
            </Tooltip>
          </div>

          {!plannedSubmittal && (
            <Button
              loading={loading}
              onClick={() => {
                createPlannedSubmittal();
              }}
              className="border rounded-md text-sm pt-0 pb-0 h-[24px]"
              disabled={cannotCreateSubmittal || loading}
              title={
                cannotCreateSubmittal ? ErrorMessages.PermissionNotGranted : ""
              }
            >
              Add
            </Button>
          )}

          {!cannotCreateSubmittal && plannedSubmittal && (
            <DeleteOutlined
              className={
                cannotCreateSubmittal || loading
                  ? "cursor-pointer py-2.5 text-slate-50"
                  : "cursor-pointer py-2.5 hover:font-medium"
              }
              onClick={() => {
                if (cannotCreateSubmittal || loading) return;
                Modal.confirm({
                  title: "Delete Submittal Plan?",
                  content:
                    "Are you sure you want to delete the Submittal Plan?",
                  okText: "Delete",
                  okType: "primary",
                  async onOk() {
                    try {
                      await deletePlannedSubmittal();
                    } catch (error) {
                      message.error("Failed to delete Submittal Plan");
                      throw error; // keeps modal open if needed
                    }
                  }
                });
              }}
              disabled={cannotCreateSubmittal || loading}
              // DeleteOutlined renders only when !cannotCreateSubmittal (guard
              // above), so the permission-denied (truthy) arm is unreachable here.
              /* v8 ignore next 3 */
              title={
                cannotCreateSubmittal ? ErrorMessages.PermissionNotGranted : ""
              }
            />
          )}
        </div>

        {/* Body */}
        {plannedSubmittal && (
          <div className="space-y-[8px]">
            {/* Prep & Review Time */}
            <div className="grid grid-cols-2 gap-[8px] text-ciq-gray-900-a85 font-medium">
              {plannedSubmittal.offsets.map((offset) => (
                <div key={offset.id}>
                  <span className="block mb-[8px]">{offset.name}</span>
                  <div className="flex items-center gap-[8px]">
                    <Input
                      disabled={cannotCreateSubmittal || loading}
                      value={offset.duration}
                      placeholder="0"
                      type="number"
                      suffix="Days"
                      onKeyDown={restrictInputToNumbers}
                      onChange={(e) => onOffsetDurationChange(e, offset.id)}
                      min={0}
                      data-testid="submittal-planned-panel-input-days"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Planned Dates */}
            <div>
              <div className="text-xs font-medium text-ciq-gray-900-a85 mb-[8px]">
                Planned Dates
              </div>

              <Spin
                spinning={!!isRecalculating || isUpdatingOffsets}
                size="small"
              >
                <div className="space-y-[8px] text-xs text-ciq-gray-800">
                  {plannedSubmittal.milestonesDates.map((milestone) => (
                    <div key={milestone.name} className="flex justify-between">
                      <span
                        className="italic"
                        style={{ color: "var(--ciq-text-black-light)" }}
                      >
                        {milestone.name}
                      </span>
                      <span className="text-black text-opacity-80 font-semibold">
                        {milestone.planned_date
                          ? DateUtils.format(milestone.planned_date)
                          : "-"}
                      </span>
                    </div>
                  ))}
                </div>
              </Spin>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (compact) {
    return cardContent;
  }

  return (
    <div className="flex pl-4 pt-16" data-testid="no-submittal-card-wrapper">
      {cardContent}
      <div className="relative w-24" data-testid="no-submittal-card-connector">
        <div className="absolute flex top-[27px]">
          <div className="border-0 border-t border-solid relative shrink-0 border-ciq-gray-300 w-20" />
          <div className="border-0 border-l border-solid relative shrink-0 w-0 h-24 -my-14" />
          <div className="border-0 border-t border-solid relative shrink-0 w-[100px]" />
        </div>
      </div>
    </div>
  );
}

export default NoSubmittalOrPlannedSubmittalLink;
